import AgreementSignModal from '@/components/config_components/AgreementSignModal/AgreementSignModal.vue';

import {
  ACTIVATION_BADGE_CLASSES,
  ANSWER_DOT_CLASSES,
  get_journey_dot_style,
  get_mapped_value,
  PROGRESS_COLORS,
  RISK_BADGE_CLASSES
} from '@/views/onboarding_system_views/onboarding_ui_helpers';

import {
  build_agreement_rows,
  build_case_form_sections,
  build_contact_rows,
  build_profile_rows,
  resolve_workflow_display_stage,
  build_summary_rows,
  build_stage_timeline,
  fetch_onboarding_cases,
  get_journey_stats,
  hydrate_case_detail
} from '@/services/onboarding_journey_service';
import {
  tablePaginationComputed,
  tablePaginationMethods,
  tablePaginationState
} from '@/services/table_pagination_service';

const EMPLOYEES_STATE = {
    active_filter: 'all',
    selected_employee: null,
    is_loading_cases: false,
    cases_error: '',
    summary_rows: [],
    profile_rows: [],
    contact_rows: [],
    agreement_rows: [],
    is_detail_fullscreen: false,
    active_detail_tab: 'summary',
    sign_modal: {
      visible: false,
      agreement_title: 'Employment contract',
      signatory_name: '',
      document_id: 'DOC-EMPLOYEE-2026'
    },
    detail_tabs: [
      { id: 'summary', label: 'Summary', icon: 'bi-grid' }
    ],
    case_form_sections: [],
    stage_timeline: [],
    stats: [
      { label: 'Active', value: '0', detail: 'Onboardings', tone: 'text-black' },
      { label: 'Blocked', value: '0', detail: 'Resolve to proceed', tone: 'text-danger' },
      { label: 'Ready', value: '0', detail: 'Activate today', tone: 'text-success' },
      { label: 'Avg time', value: '-', detail: 'To activation', tone: 'text-black' }
    ],
    journey_filters: [
      { label: 'All', count: '18', tone: 'all' },
      { label: 'Supplier', tone: 'supplier' },
      { label: 'Employee', tone: 'employee' },
      { label: 'Merchant', tone: 'merchant' },
      { label: 'Partner', tone: 'partner' },
      { label: 'Intern', tone: 'intern' },
      { label: 'Client', tone: 'client' }
    ],
    employee_cases: []
  };

export default {
  name: 'OnboardingManageEmployees',
  components: {
    AgreementSignModal
  },
  data: () => ({
    ...EMPLOYEES_STATE,
    ...tablePaginationState()
  }),
  created() {
    this.load_employee_cases();
  },
  computed: {
    filtered_employee_cases() {
      return this.filter_table_rows(this.filter_cases_by_journey(this.employee_cases, this.active_filter));
    },
    table_total_rows() {
      return this.filtered_employee_cases.length;
    },
    paginated_employee_cases() {
      return this.paginate_table_rows(this.filtered_employee_cases);
    },
    ...tablePaginationComputed,
    active_form_section_tab() {
      return this.detail_tabs.find((tab) => tab.id === this.active_detail_tab && tab.id !== 'summary') || null;
    },
    active_form_section_is_document_section() {
      const section_label = String(this.active_form_section_tab?.section?.label || '').trim().toLowerCase();
      const form_name = String(this.active_form_section_tab?.form_name || '').trim().toLowerCase();

      return ['document', 'upload', 'attachment', 'file', 'evidence'].some((word) => (
        section_label.includes(word) || form_name.includes(word)
      ));
    }
  },
  methods: {
    build_detail_tabs(case_form_sections = []) {
      const document_section_tabs = [];
      const other_section_tabs = [];

      case_form_sections.forEach((form, form_index) => {
        form.sections.forEach((section, section_index) => {
          const tab = {
            id: `section-${form_index}-${section_index}-${section.id}`,
            label: section.label,
            icon: 'bi-list-check',
            form_name: form.form_name,
            section
          };

          const section_label = String(section?.label || '').trim().toLowerCase();
          const form_name = String(form?.form_name || '').trim().toLowerCase();
          const is_document_section = ['document', 'upload', 'attachment', 'file', 'evidence']
            .some((word) => section_label.includes(word) || form_name.includes(word));

          if (is_document_section) {
            tab.icon = 'bi-folder';
            document_section_tabs.push(tab);
            return;
          }

          other_section_tabs.push(tab);
        });
      });

      return [
        { id: 'summary', label: 'Summary', icon: 'bi-grid' },
        ...document_section_tabs,
        ...other_section_tabs
      ];
    },
    async load_employee_cases() {
      this.is_loading_cases = true;
      this.cases_error = '';

      try {
        const cases = await fetch_onboarding_cases('employee');
        const normalized_cases = cases.map((case_item) => {
          const workflow_stage = resolve_workflow_display_stage(case_item);

          return workflow_stage
            ? {
                ...case_item,
                current_workflow_status: workflow_stage,
                stage: workflow_stage,
                detail: workflow_stage
              }
            : case_item;
        });

        this.employee_cases = normalized_cases;
        this.stats = get_journey_stats(normalized_cases, 'employee');
      } catch (error) {
        this.cases_error = error?.response?.data?.message || error?.message || 'Unable to load employee onboardings.';
        this.employee_cases = [];
        this.stats = get_journey_stats([], 'employee');
      } finally {
        this.is_loading_cases = false;
      }
    },
    case_matches_filter(case_item, tone) {
      if (tone === 'all') return true;

      const normalized_tone = String(tone || '').trim().toLowerCase();
      const journey_values = [
        case_item?.journey_tone,
        case_item?.journey_key,
        case_item?.journey,
        case_item?.entity_type_name,
        case_item?.current_entity_type_name
      ]
        .map((value) => String(value || '').trim().toLowerCase())
        .filter(Boolean);

      if (journey_values.includes(normalized_tone)) return true;
      return journey_values.some((value) => value.includes(normalized_tone));
    },
    filter_cases_by_journey(cases = [], tone = 'all') {
      return cases.filter((case_item) => this.case_matches_filter(case_item, tone));
    },
    set_active_filter(tone) {
      this.active_filter = tone;
      this.reset_table_page();
    },
    ...tablePaginationMethods,
    async open_employee_detail(case_item) {
      this.selected_employee = case_item;
      this.active_detail_tab = 'summary';
      this.is_detail_fullscreen = false;
      this.summary_rows = build_summary_rows(case_item);
      this.profile_rows = build_profile_rows(case_item);
      this.case_form_sections = build_case_form_sections(case_item);
      this.detail_tabs = this.build_detail_tabs(this.case_form_sections);
      try {
        const hydrated_case = await hydrate_case_detail(case_item);
        const summary_rows = build_summary_rows(hydrated_case);
        const profile_rows = build_profile_rows(hydrated_case);
        const contact_rows = build_contact_rows(hydrated_case);
        const agreement_rows = build_agreement_rows(hydrated_case);
        const stage_timeline = build_stage_timeline(hydrated_case);
        const case_form_sections = build_case_form_sections(hydrated_case);

        this.selected_employee = hydrated_case;

        if (summary_rows.length) {
          this.summary_rows = summary_rows;
        }

        if (profile_rows.length) {
          this.profile_rows = profile_rows;
        }

        if (contact_rows.length) {
          this.contact_rows = contact_rows;
        }

        if (agreement_rows.length) {
          this.agreement_rows = agreement_rows;
        }

        if (stage_timeline.length) {
          this.stage_timeline = stage_timeline;
        }

        this.case_form_sections = case_form_sections;
        this.detail_tabs = this.build_detail_tabs(case_form_sections);
      } catch {
        this.selected_employee = case_item;
      }
    },
    close_employee_detail() {
      this.selected_employee = null;
      this.is_detail_fullscreen = false;
    },
    set_detail_tab(tab_id) {
      this.active_detail_tab = tab_id;
    },
    toggle_detail_fullscreen() {
      this.is_detail_fullscreen = !this.is_detail_fullscreen;
    },
    open_sign_modal(agreement_title = 'Employment contract', signatory_name = '') {
      this.sign_modal = {
        visible: true,
        agreement_title,
        signatory_name: signatory_name || this.selected_employee?.name || '',
        document_id: 'DOC-EMPLOYEE-2026'
      };
    },
    close_sign_modal() {
      this.sign_modal.visible = false;
    },
    complete_signature() {
      this.close_sign_modal();
    },
    journey_dot_style(tone) {
      return get_journey_dot_style(tone);
    },
    progress_bar_color(tone) {
      return get_mapped_value(PROGRESS_COLORS, tone, '#416f6e');
    },
    risk_badge_class(tone) {
      return get_mapped_value(RISK_BADGE_CLASSES, tone, 'bg-secondary-subtle text-secondary-emphasis');
    },
    activation_badge_class(tone) {
      return get_mapped_value(ACTIVATION_BADGE_CLASSES, tone, 'bg-secondary-subtle text-secondary-emphasis');
    },
    answer_dot_class(tone) {
      return get_mapped_value(ANSWER_DOT_CLASSES, tone, 'bg-secondary');
    },
    should_show_sub_section_title(sub_section) {
      const sub_section_label = String(sub_section?.label || '').trim().toLowerCase();
      const section_label = String(this.active_form_section_tab?.section?.label || '').trim().toLowerCase();

      if (!sub_section_label) return false;
      return sub_section_label !== section_label && sub_section_label !== 'details';
    },
    is_document_question(question = {}) {
      const label = String(question?.label || '').trim().toLowerCase();
      const answer = String(question?.answer || '').trim();

      if (/^(https?:\/\/|www\.)/i.test(answer)) {
        return true;
      }

      return ['document', 'upload', 'attachment', 'file', 'certificate', 'id copy', 'proof', 'bank statement']
        .some((word) => label.includes(word));
    },
    document_answer_meta(question = {}) {
      const raw_answer = String(question?.answer || '').trim();
      const looks_like_url = /^(https?:\/\/|www\.)/i.test(raw_answer);
      const normalized_url = looks_like_url && !/^https?:\/\//i.test(raw_answer)
        ? `https://${raw_answer}`
        : raw_answer;
      const normalized_answer = raw_answer.toLowerCase();
      const uploaded = Boolean(raw_answer) && !['not answered', 'not uploaded', 'pending'].includes(normalized_answer);

      const document_meta = {
        name: raw_answer || 'Not uploaded',
        url: looks_like_url ? normalized_url : '',
        uploaded
      };

      return document_meta;
    },
    document_status_meta(question = {}) {
      const document_meta = this.document_answer_meta(question);

      if (document_meta.uploaded) {
        const uploaded_status = {
          label: 'OK',
          pill_class: 'bg-success-subtle text-success-emphasis',
          icon_class: 'onboarding-document-status-icon is-success',
          icon: 'bi-check-lg'
        };

        return uploaded_status;
      }

      const missing_status = {
        label: 'Missing',
        pill_class: 'bg-danger-subtle text-danger-emphasis',
        icon_class: 'onboarding-document-status-icon is-danger',
        icon: 'bi-x-lg'
      };

      return missing_status;
    },
    section_tone(label = '') {
      const normalized_label = String(label || '').trim().toLowerCase();

      if (['document', 'upload', 'attachment', 'file', 'evidence'].some((word) => normalized_label.includes(word))) {
        return 'danger';
      }
      if (['bank', 'payment', 'salary', 'account'].some((word) => normalized_label.includes(word))) {
        return 'teal';
      }
      if (['employment', 'role', 'manager', 'job'].some((word) => normalized_label.includes(word))) {
        return 'amber';
      }
      if (['residential', 'address', 'location', 'city'].some((word) => normalized_label.includes(word))) {
        return 'navy';
      }
      if (['emergency', 'contact', 'next of kin'].some((word) => normalized_label.includes(word))) {
        return 'success';
      }
      if (['personal', 'profile', 'identity', 'bio'].some((word) => normalized_label.includes(word))) {
        return 'teal';
      }

      return 'navy';
    },
    journey_filter_count(filter) {
      return this.filter_cases_by_journey(this.employee_cases, filter.tone).length;
    }
  }
};


